/**
 * ProductionTab — production photo updates + AI quality assessment.
 *
 * Photos are CSS-painted placeholders (we don't ship real factory photos).
 * Each photo gets a Claude-vision-style summary + flag list.
 */
import React from 'react';
import { Camera, Sparkles, AlertCircle, CheckCircle2, Calendar, Shield } from 'lucide-react';
import { useManifest } from '../ManifestContext';
import { getOrderMocks } from '../mocks';

const formatDate = (iso) => new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

const ProductionTab = () => {
  const { activeOrder } = useManifest();
  const mocks = getOrderMocks(activeOrder.id);
  const updates = mocks.production;
  const factory = activeOrder.factories?.find((f) => f.id === activeOrder.pickedQuoteFactoryId);

  const flagCount = updates.reduce((s, u) => s + (u.ai?.flags?.length || 0), 0);

  return (
    <div className="manifest-tab-pane">
      <div className="manifest-insights-row">
        <div className="manifest-insight-card phase-4">
          <div className="manifest-insight-icon"><Camera size={16} /></div>
          <div>
            <div className="manifest-insight-label">Updates received</div>
            <div className="manifest-insight-value">{updates.length}</div>
            <div className="manifest-insight-sub">From {factory?.name || '—'}</div>
          </div>
        </div>
        <div className="manifest-insight-card phase-4">
          <div className="manifest-insight-icon"><Sparkles size={16} /></div>
          <div>
            <div className="manifest-insight-label">AI quality flags</div>
            <div className="manifest-insight-value">{flagCount}</div>
            <div className="manifest-insight-sub">{flagCount === 0 ? 'No concerns flagged' : 'Review highlighted updates'}</div>
          </div>
        </div>
        <div className="manifest-insight-card phase-4">
          <div className="manifest-insight-icon"><Shield size={16} /></div>
          <div>
            <div className="manifest-insight-label">Inspection scheduled</div>
            <div className="manifest-insight-value">SGS · Day 30</div>
            <div className="manifest-insight-sub">Auto-booked by Co-Pilot</div>
          </div>
        </div>
      </div>

      {updates.length === 0 ? (
        <div className="manifest-empty manifest-empty-inline">
          <p>No production photos yet — they'll arrive via WhatsApp from the factory and post here automatically.</p>
        </div>
      ) : (
        <div className="production-tab-list">
          {updates.map((u) => (
            <div key={u.id} className="production-update-card">
              <div className="production-update-photo" style={{ background: u.tone }}>
                <div className={`production-photo-pattern pattern-${u.placeholder}`} />
                <div className="production-photo-stamp">
                  <Calendar size={11} /> {formatDate(u.ts)}
                </div>
              </div>
              <div className="production-update-body">
                <div className="production-update-head">
                  <span className="production-update-caption">{u.caption}</span>
                  <span className="production-update-source">{u.sender}</span>
                </div>
                <div className="production-ai-block">
                  <div className="production-ai-head">
                    <Sparkles size={12} /> AI quality check
                    <span className="production-ai-conf">conf {Math.round(u.ai.confidence * 100)}%</span>
                  </div>
                  <p className="production-ai-summary">{u.ai.summary}</p>
                  {u.ai.flags.length === 0 ? (
                    <div className="production-ai-flag clean"><CheckCircle2 size={11} /> No defects detected</div>
                  ) : (
                    <ul className="production-ai-flag-list">
                      {u.ai.flags.map((f, i) => (
                        <li key={i} className={`production-ai-flag severity-${f.severity}`}>
                          <AlertCircle size={11} /> {f.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductionTab;
